import React from 'react';
import { Link } from 'react-router-dom';

const obligatedEntities = [
  { sector: 'Cement', status: 'Active', threshold: 'Plants above notified production thresholds', detail: 'GHG intensity targets notified for major plants. Reduction range 0.85%–7.6%.' },
  { sector: 'Aluminium', status: 'Active', threshold: 'Primary smelter operations above threshold', detail: 'Reduction range 1.9%–7.06%.' },
  { sector: 'Pulp & Paper', status: 'Active', threshold: 'Large mills above threshold', detail: 'Up to 15% reduction across first cycle.' },
  { sector: 'Chlor-Alkali', status: 'Active', threshold: 'Chlorine plants above threshold', detail: 'Reduction range 1.32%–4.53%.' },
  { sector: 'Iron & Steel', status: 'Active', threshold: 'Integrated steel plants', detail: 'Additional target pathway expected.' },
  { sector: 'Petroleum Refinery', status: 'Active', threshold: 'Refineries above notified capacity', detail: 'Final targets notified January 2026.' },
  { sector: 'Petrochemicals', status: 'Active', threshold: 'Notified chemical plants', detail: 'Final targets notified January 2026.' },
  { sector: 'Fertilisers', status: 'Active', threshold: 'Large fertiliser units', detail: 'Target expansion expected.' },
  { sector: 'Textiles', status: 'Active', threshold: 'Large processing and weaving units', detail: 'Final targets notified January 2026.' },
];

const voluntaryParticipants = [
  {
    type: 'Renewable Energy Project Developers',
    color: 'var(--neon-green)',
    examples: 'Solar farms, wind projects, small hydro, biogas',
    howToJoin: 'Register project under BEE-OM-004 or relevant RE methodology. Get ACVA verification. Receive Voluntary CCCs.',
  },
  {
    type: 'Agroforestry & Forestry Projects',
    color: 'var(--neon-cyan)',
    examples: 'Agroforestry, reforestation, sustainable land management',
    howToJoin: 'Register under BEE-OM-001 (Afforestation) or equivalent. Sequestration projects earn CCCs after verification.',
  },
  {
    type: 'Waste Management Projects',
    color: 'var(--neon-orange)',
    examples: 'Landfill gas capture, composting, wastewater treatment',
    howToJoin: 'Register under appropriate waste methodology. Methane avoidance or capture earns CCCs.',
  },
  {
    type: 'CCUS Projects',
    color: 'var(--neon-pink)',
    examples: 'Carbon capture and utilisation or storage',
    howToJoin: 'CCUS-specific methodology under development. Industrial decarbonisation pilot projects eligible.',
  },
  {
    type: 'SMEs & Non-Obligated Companies',
    color: 'var(--neon-yellow)',
    examples: 'Any company below CCTS thresholds with qualifying emission-reduction activities',
    howToJoin: 'Identify qualifying activities, register project with BEE, appoint ACVA verifier, earn Voluntary CCCs on performance.',
  },
  {
    type: 'NGOs & Community Projects',
    color: 'var(--neon-blue)',
    examples: 'Community forestry, rural clean energy, sustainable agriculture',
    howToJoin: 'NGOs can be project developers or co-developers. Register under applicable methodology. Credits benefit project communities.',
  },
];

const voluntaryBuyers = [
  { type: 'Corporate Sustainability Teams', reason: 'Offset Scope 1/2/3 emissions, meet SBTi or net-zero commitments, ESG reporting' },
  { type: 'SMEs seeking ESG credibility', reason: 'Voluntary offsetting for supply chain requirements or customer-facing sustainability claims' },
  { type: 'Event organisers', reason: 'Carbon-neutral event certification' },
  { type: 'Financial institutions', reason: 'Green portfolio alignment, sustainability-linked products' },
  { type: 'Individuals', reason: 'Personal carbon footprint offsetting (via platform once fully operational)' },
];

export default function WhoCanParticipate() {
  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag" style={{ color: 'var(--neon-pink)' }}>Learn</div>
          <h1 className="section-title">Who Can Participate in India's Carbon Market?</h1>
          <p className="section-copy">
            India's carbon market has three types of participants: obligated compliance entities, voluntary project developers who earn credits, and voluntary buyers who purchase credits to offset their footprint.
          </p>
        </div>

        <div className="metric-strip" style={{ marginTop: '1.5rem' }}>
          <div className="metric-tile">
            <div className="eyebrow">Obligated sectors</div>
            <div className="value" style={{ color: 'var(--neon-orange)' }}>9</div>
            <div className="copy-sm">Mandatory compliance</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Offset project types</div>
            <div className="value" style={{ color: 'var(--neon-green)' }}>6+</div>
            <div className="copy-sm">Voluntary developer pathways</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Voluntary buyers</div>
            <div className="value" style={{ color: 'var(--neon-cyan)' }}>Open</div>
            <div className="copy-sm">Any entity or individual</div>
          </div>
        </div>
      </section>

      {/* Obligated entities */}
      <section className="section-stack">
        <div className="section-tag" style={{ color: 'var(--neon-orange)' }}>Track 1 — Compliance</div>
        <h2 className="headline-sm" style={{ fontSize: '1.35rem' }}>Obligated sectors under CCTS</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.88rem', maxWidth: '640px' }}>These sectors have legally binding GHG intensity targets. If your plant is in one of these sectors and above the notified threshold, you are required to participate.</p>

        <div style={{ display: 'grid', gap: '0.6rem', marginTop: '1rem' }}>
          {obligatedEntities.map((entity) => (
            <div key={entity.sector} className="surface-panel" style={{ padding: '1.1rem 1.4rem', display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: '1rem', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.9rem' }}>{entity.sector}</div>
                <span className="badge" style={{ marginTop: '0.3rem', color: 'var(--neon-orange)', background: 'rgba(255,102,0,0.08)', borderColor: 'transparent' }}>{entity.status}</span>
              </div>
              <div style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>{entity.threshold}</div>
              <div style={{ color: 'var(--muted2)', fontSize: '0.8rem', fontFamily: 'Space Mono, monospace' }}>{entity.detail}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Voluntary project developers */}
      <section className="section-stack">
        <div className="section-tag" style={{ color: 'var(--neon-green)' }}>Track 2 — Offset (Project Developers)</div>
        <h2 className="headline-sm" style={{ fontSize: '1.35rem' }}>Who can develop offset projects and earn CCCs?</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.88rem', maxWidth: '640px' }}>Any entity — company, NGO, or individual — with a qualifying emission-reduction project can register under BEE's offset mechanism to earn Voluntary CCCs.</p>

        <div className="section-grid-2" style={{ marginTop: '1rem' }}>
          {voluntaryParticipants.map((item) => (
            <div key={item.type} className="surface-panel" style={{ padding: '1.4rem', borderLeft: `3px solid ${item.color}` }}>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.92rem', color: item.color, marginBottom: '0.5rem' }}>{item.type}</div>
              <div className="stat-label" style={{ marginBottom: '0.3rem' }}>Examples</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.8rem' }}>{item.examples}</div>
              <div className="stat-label" style={{ marginBottom: '0.3rem' }}>How to join</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.82rem', lineHeight: '1.7' }}>{item.howToJoin}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Voluntary buyers */}
      <section className="section-stack">
        <div className="section-tag" style={{ color: 'var(--neon-cyan)' }}>Track 3 — Voluntary Buyers</div>
        <h2 className="headline-sm" style={{ fontSize: '1.35rem' }}>Who can buy CCCs voluntarily?</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.88rem', maxWidth: '640px' }}>Beyond obligated entities, any organisation or individual can purchase CCCs to voluntarily offset emissions or meet sustainability commitments.</p>

        <div style={{ display: 'grid', gap: '0.6rem', marginTop: '1rem' }}>
          {voluntaryBuyers.map((buyer) => (
            <div key={buyer.type} className="surface-panel" style={{ padding: '1rem 1.4rem', display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1.5rem', alignItems: 'start' }}>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '0.88rem', color: 'var(--neon-cyan)' }}>{buyer.type}</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.84rem', lineHeight: '1.65' }}>{buyer.reason}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="surface-panel" style={{ padding: '1.8rem', background: 'rgba(0,229,255,0.04)', borderColor: 'rgba(0,229,255,0.2)' }}>
        <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.6rem' }}>Explore the market on CarbonX</div>
        <p style={{ color: 'var(--muted)', fontSize: '0.87rem', marginBottom: '1.2rem' }}>Browse active project listings, simulate trading scenarios, or register your project in the discovery directory.</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/listings" className="btn btn-primary">Browse Listings</Link>
          <Link to="/trade" className="btn btn-secondary">Open Simulator</Link>
          <Link to="/signup" className="btn btn-secondary">Create Account</Link>
        </div>
      </section>

      <section style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/learn/how-cccs-work" className="btn btn-secondary">How CCCs Work</Link>
        <Link to="/learn/compliance-vs-offset" className="btn btn-secondary">Compliance vs Offset</Link>
        <Link to="/market/india" className="btn btn-secondary">India Market Overview</Link>
      </section>
    </div>
  );
}
